import { View, Pressable, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import type { DirtyStateConfig, ActionBarAction } from "./types";
import { HEADER } from "@/components/Header/constants";

type ActionZoneProps = {
  onRefresh?: () => void;
  dirty?: DirtyStateConfig;
  actions?: ActionBarAction[];
};

export function ActionZone({ onRefresh, dirty, actions }: ActionZoneProps) {
  const { colors } = useTheme();

  const renderButton = (action: ActionBarAction) => {
    const isPrimary = action.variant === "primary";
    const isDanger = action.variant === "danger";
    const disabled = action.disabled;

    let bg = "transparent";
    let fg = colors.text;
    let border = colors.border;

    if (disabled) {
      bg = "transparent";
      fg = colors.textSecondary;
      border = colors.border;
    } else if (isPrimary) {
      bg = colors.green?.background || "#dcfce7";
      fg = colors.green?.text || "#16a34a";
      border = colors.border;
    } else if (isDanger) {
      bg = colors.red?.background || "#fef2f2";
      fg = colors.red?.text || "#dc2626";
      border = colors.red?.border || "#fecaca";
    }

    return (
      <Button
        key={action.id}
        text={action.label}
        icon={action.icon}
        iconSize={15}
        disabled={disabled}
        onPress={action.onPress}
        style={[styles.actionBtn, { backgroundColor: bg, borderColor: border }, disabled && { opacity: 0.5 }]}
        textStyle={{ fontSize: 12, fontWeight: "600", color: fg }}
        iconStyle={{ color: fg }}
      />
    );
  };

  return (
    <View style={styles.container}>
      {/* Refresh button (icon only) */}
      {onRefresh ? (
        <Button
          icon="refresh"
          iconSize={16}
          onPress={onRefresh}
          style={[styles.iconBtn, { borderColor: colors.border }]}
          iconStyle={{ color: colors.textSecondary }}
        />
      ) : null}

      {/* Dirty state: indicator + Discard + Save */}
      {dirty && dirty.count > 0 ? (
        <>
          <View
            style={[
              styles.dirtyIndicator,
              {
                backgroundColor: colors.yellow?.background || "#fef9c3",
                borderColor: colors.yellow?.border || "#fde047",
              },
            ]}
          >
            <MaterialCommunityIcons name="circle-edit-outline" size={14} color={colors.yellow?.text || "#ca8a04"} />
            <ThemedText style={[styles.dirtyText, { color: colors.yellow?.text || "#ca8a04" }]}>
              {dirty.count} unsaved change{dirty.count !== 1 ? "s" : ""}
            </ThemedText>
          </View>
          <Pressable
            onPress={dirty.onDiscard}
            style={({ hovered }: any) => [
              styles.discardBtn,
              { borderColor: colors.border },
              hovered && { backgroundColor: colors.backgroundSecondary },
            ]}
          >
            <ThemedText style={[styles.discardText, { color: colors.textSecondary }]}>Discard</ThemedText>
          </Pressable>
          <Button
            text="Save"
            icon="content-save"
            iconSize={15}
            onPress={dirty.onSave}
            style={[styles.actionBtn, { backgroundColor: colors.primary, borderWidth: 0 }]}
            textStyle={{ fontSize: 12, fontWeight: "600", color: "#fff" }}
            iconStyle={{ color: "#fff" }}
          />
        </>
      ) : null}

      {/* Page actions */}
      {actions?.map((action) => renderButton(action))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  actionBtn: {
    height: HEADER.HEIGHT,
    paddingHorizontal: 8,
    borderRadius: HEADER.RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
    gap: 4,
  },
  iconBtn: {
    width: HEADER.ICON_SIZE,
    height: HEADER.ICON_SIZE,
    paddingHorizontal: 0,
    borderRadius: HEADER.RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
    backgroundColor: "transparent",
  },
  dirtyIndicator: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 8,
    height: HEADER.HEIGHT,
    borderRadius: HEADER.RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
  },
  dirtyText: {
    fontSize: 12,
    fontWeight: "600",
  },
  discardBtn: {
    paddingHorizontal: 8,
    height: HEADER.HEIGHT,
    justifyContent: "center",
    borderRadius: HEADER.RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
  },
  discardText: {
    fontSize: 12,
    fontWeight: "500",
  },
});
